<template>
    <div :class="['hubspot-chat', {'customer-support-only' : !hasAccessToChatBot}]">
        <template v-if="widgetLoaded">
            <div class="hubspot-chat__button_container">
                <button @click="startNewThread"><img :src="hubSpotNewThreadImage" class="hubspot-new-thread"  aria-label="Start a new chat"></button>
                <button class="icon-minimize" @click="hideChat" aria-label="Hide the chat"></button>
            </div>
        </template>
        <div :id="wrapper_id" class="hubspot-chat__wrapper"></div>
        <div class="hubspot-chat__footer">
            <span class="hubspot-chat__footer-text" v-html="getFooterText"></span>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import MobileHelper from 'components/chatbot/assistant/helpers/MobileHelper'

    export default {
        name: 'HubSpotChat',
        mixins: [ MobileHelper ],
        props: {
            wrapper_id: {
                type: String,
                required: true,
            }
        },
        data () {
            return {
                hubSpotNewThreadImage: require('@/assets/images/chatbot/hubspot_new_thread.svg'),
                widgetLoaded: false
            }
        },
        computed: {
            ...mapGetters({
                areStatisticsCookiesEnabled: 'Hubspot/hubspotChatIsAvailable',
                isLoggedIn: 'isLoggedIn',
                visitorIdentificationToken: 'Hubspot/visitorIdentificationToken',
                userInfo: 'User/getUserInfo',
            }),
            hasAccessToChatBot () {
                return this.isLoggedIn && this.userInfo && this.userInfo.hasAccessToChat
            },
            getFooterText () {
                return this.$t('ChatBotCombo.customer_support_footer_text')
            }
        },
        mounted () {
            if (!this.areStatisticsCookiesEnabled) {
                return
            }
            this.loadChat()

            window.addEventListener('resize', this.updateViewportHeight)
            this.updateViewportHeight()
        },
        methods: {
            async loadChat () {
                if (this.isLoggedIn) {
                    if (this.visitorIdentificationToken && this.userInfo.email) {
                        window.hsConversationsSettings.identificationEmail = this.userInfo.email
                        window.hsConversationsSettings.identificationToken = this.visitorIdentificationToken
                    } else {
                        return await this.triggerVisitorIdentificationTokenGeneration()
                    }
                }

                // The main hsConversationsSettings object should be set in main.html file initially.
                window.hsConversationsSettings.disableInitialInputFocus = true
                window.hsConversationsSettings.inlineEmbedSelector = '#' + this.wrapper_id
                let loadHsChat = () => {
                    window.HubSpotConversations.widget.load({ widgetOpen: false })
                    window.HubSpotConversations.on('unreadConversationCountChanged', payload => {
                        this.$store.dispatch('Hubspot/UnreadConversationCountChanged', payload.unreadCount)
                    })
                    window.HubSpotConversations.on('widgetLoaded', payload => {
                        this.setWidgetLoaded()
                    })
                }
                if (window.HubSpotConversations) {
                    if (window.HubSpotConversations.widget && window.HubSpotConversations.widget.status().loaded) {
                        window.HubSpotConversations.clear({ resetWidget: true })
                    }
                    loadHsChat()
                } else {
                    window.hsConversationsOnReady = [loadHsChat]
                }
            },
            toggleConfirmModalQuestion () {
                this.$refs.resetChat.$el.classList.toggle('slide-down')
            },
            setWidgetLoaded () {
                this.widgetLoaded = true
            },
            showStartNewThread () {
                this.toggleConfirmModalQuestion()
            },
            startNewThread () {
                window.HubSpotConversations.widget.refresh({ openToNewThread: true })
            },
            hideChat () {
                this.$emit('hide-chat-requested', true)
                setTimeout(() => this.$store.dispatch('ChatBotCombo/setCurrentChatMode', null), 200)
            },
            async triggerVisitorIdentificationTokenGeneration () {
                await this.$store.dispatch('Hubspot/GenerateVisitorIdentificationToken')
            },
            goBackToChatList () {
                this.$store.dispatch('ChatBotCombo/setCurrentChatMode', null)
            }
        },
        watch: {
            areStatisticsCookiesEnabled: function (newState, oldState) {
                if (newState) {
                    this.loadChat()
                }
            },
            isLoggedIn: function (newState, oldState) {
                if (newState && !oldState) {
                    this.loadChat()
                } else if (!newState && oldState) {
                    window.HubSpotConversations.widget.remove()
                    window.HubSpotConversations.clear()
                }
            },
            visitorIdentificationToken: function (newToken, oldToken) {
                if (newToken) {
                    this.loadChat()
                }
            }
        },
    }
</script>

<style lang="scss">
.hubspot-chat {
    width: min(500px, 100dvw - 50px);
    background: white;
    overflow: hidden;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    box-shadow: 2px 2px 8px 0 #00000040;
    height: calc(100% - 50px);

    [class="mobile_sm"] & {
        border-radius: 0;
    }

    &.customer-support-only {
        height: min(690px, 100vh - 145px);
    }

    [class="mobile_sm"] & {
        width: 100%;
        height: 100%;
        position: fixed;
        left: 0;
        z-index: 100000;

        &.customer-support-only {
            top: 0;
        }
    }

    &__button_container {
        position: absolute;
        background: $hubspot_blue;
        right: 20px;
        height: 68px;
        display: flex;
        align-items: center;
        z-index: 9999;
        display: flex;
        gap: 10px;

        .hubspot-new-thread {
            width: 18px;
            height: 18px;
            filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(192deg) brightness(102%) contrast(102%);
        }

        .icon-minimize {
            padding-bottom: 2rem;
        }

        button {
        font-size: 3.5rem;
        height: 40px;
        width: 40px;
        appearance: none;
        background: none;
        border: none;
        cursor: pointer;
        margin: 0;
        padding: 1rem;
        color: $white;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background: rgba(255, 255, 255, 0.2);
        }
}
    }

    &__restart-popup {
        position: relative;
        z-index: 9999
    }

    .hubspot-chat__wrapper {
        flex-direction: column;
        font-family: Nunito-Regular, sans-serif;
        position: relative;
        height: 100%;
        display: flex;
        z-index: 9991;
        border-bottom: 1px solid $aqua_haze;

        [class="mobile_sm"] & {
            height: calc(var(--vh, 1vh) * 100 - 50px);
        }

        > * {
            box-sizing: border-box;
            flex: 1;
            display: flex;
            justify-content: stretch;
        }

        iframe {
            flex: 1;
            border: none;
        }
    }

    &__go-back-button {
        cursor: pointer;
        padding: 8px;
        color: $white;
        border-radius: 3px;
        font-size: 2rem;
        margin-right: auto;

        &:hover {
            background: rgba(255, 255, 255, 0.2);
        }
    }

    .chat-title {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
    }

    &__footer {
        padding: 13px 25px 10px 35px;
        text-align: center;
        background: white;
        position: absolute;
        width: 100%;
        box-shadow: 2px 2px 8px 0 #00000040;
        border-bottom-left-radius: 1rem;
        border-bottom-right-radius: 1rem;
        z-index: 10000;
        display: flex;

            [class="mobile_sm"] & {
                line-height: 13px;
                border-radius: 0;
            }

            a {
                text-decoration: underline;
            }
    }

    &__footer-text {
        width: 100%;
        justify-content: center;
        font-size: 1.2rem;
        color: $dove_gray;

        a {
            color: $dove_gray !important;
        }

        [class="mobile_sm"] & {
            font-size: 1rem;
        }
    }
}
</style>
